// src/finance/components/TaxSettingsPanel.tsx
// Accordion des paramètres fiscaux (spec § 2.1) : tout est modifiable,
// les défauts vérifiés 2026 se restaurent d'un clic.
import React, { useState } from 'react'
import type { FinanceTaxSettings } from '../engine/types'
import { DEFAULT_FINANCE_SETTINGS } from '../engine/defaults'
import { parseAmount } from '../format'
import { InfoTip } from './InfoTip'
import { TAX_INFO } from './taxInfo'

const fieldStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
  fontSize: '0.75rem',
  color: 'var(--muted)',
}

const inputStyle: React.CSSProperties = {
  background: 'var(--bg)',
  border: '1px solid var(--line)',
  borderRadius: '6px',
  color: 'var(--fg)',
  fontSize: '0.8125rem',
  fontFamily: 'var(--mono)',
  padding: '0.375rem 0.5rem',
  width: '110px',
}

/** Champ pourcentage : affiche rate × 100, émet la fraction. */
function PercentField({
  label,
  value,
  onChange,
  info,
}: {
  label: string
  value: number
  onChange: (v: number) => void
  /** Texte d'infobulle. L'icône est un bouton SVG sans textContent : le libellé reste exact. */
  info?: string | undefined
}) {
  return (
    <label style={fieldStyle}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
        {label}
        {info === undefined ? null : <InfoTip label={label} text={info} />}
      </span>
      <span>
        <input
          style={inputStyle}
          defaultValue={String(Math.round(value * 10000) / 100)}
          inputMode="decimal"
          onBlur={(e) => {
            const parsed = parseAmount(e.target.value)
            if (parsed !== null && parsed <= 100) onChange(parsed / 100)
            else e.target.value = String(Math.round(value * 10000) / 100)
          }}
        />{' '}
        %
      </span>
    </label>
  )
}

/** Champ montant EUR. */
function AmountField({
  label,
  value,
  onChange,
}: {
  label: string
  value: number
  onChange: (v: number) => void
}) {
  return (
    <label style={fieldStyle}>
      {label}
      <span>
        <input
          style={inputStyle}
          defaultValue={String(value)}
          inputMode="decimal"
          onBlur={(e) => {
            const parsed = parseAmount(e.target.value)
            if (parsed !== null) onChange(parsed)
            else e.target.value = String(value)
          }}
        />{' '}
        €
      </span>
    </label>
  )
}

export function TaxSettingsPanel({
  tax,
  onChange,
}: {
  tax: FinanceTaxSettings
  onChange: (tax: FinanceTaxSettings) => void
}) {
  const [open, setOpen] = useState(false)
  // Clé de remontage : SEUL le reset remonte le corps du panneau. Remonter à chaque
  // changement de contenu avalait le premier clic : le mousedown déclenche le blur du
  // champ en cours, qui commit la valeur, donc change la clé — et React démonte le
  // bouton cliqué avant le mouseup, si bien que le onClick ne part jamais.
  // Le reset est la seule modification externe qui doit refaire lire tous les
  // defaultValue ; les commits par onBlur, eux, écrivent déjà le DOM eux-mêmes.
  // Effet de bord bienvenu : après « 19,999 », le champ garde le texte saisi tout en
  // stockant 0.19999, au lieu de se réafficher arrondi à « 20 ».
  const [resetEpoch, setResetEpoch] = useState(0)

  const patch = (changes: Partial<FinanceTaxSettings>) => onChange({ ...tax, ...changes })

  return (
    <section
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: '10px',
        padding: '1rem',
      }}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          background: 'transparent',
          border: 'none',
          color: 'var(--fg)',
          fontSize: '0.9375rem',
          fontWeight: 600,
          fontFamily: 'inherit',
          cursor: 'pointer',
          padding: 0,
          width: '100%',
          textAlign: 'left',
        }}
      >
        {open ? '▾' : '▸'} Paramètres fiscaux
      </button>

      {open && (
        <div key={resetEpoch} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '0.75rem' }}>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <PercentField label="CT UK — taux réduit" value={tax.ctSmallRate} onChange={(v) => patch({ ctSmallRate: v })} />
            <PercentField label="CT UK — taux plein" value={tax.ctMainRate} onChange={(v) => patch({ ctMainRate: v })} />
            <AmountField label="CT UK — seuil bas (≈ £50k)" value={tax.ctLowerLimit} onChange={(v) => patch({ ctLowerLimit: v })} />
            <AmountField label="CT UK — seuil haut (≈ £250k)" value={tax.ctUpperLimit} onChange={(v) => patch({ ctUpperLimit: v })} />
          </div>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <PercentField label="PFU dividendes" value={tax.pfuRate} onChange={(v) => patch({ pfuRate: v })} />
            <PercentField
              label="Cotisations patronales"
              value={tax.employerRate}
              onChange={(v) => patch({ employerRate: v })}
              info={TAX_INFO.employerRateSetting}
            />
            <PercentField label="Cotisations salariales" value={tax.employeeRate} onChange={(v) => patch({ employeeRate: v })} />
          </div>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <PercentField label="IS français — taux réduit (comparatif)" value={tax.frIsReducedRate} onChange={(v) => patch({ frIsReducedRate: v })} />
            <AmountField label="IS français — plafond du taux réduit" value={tax.frIsReducedCap} onChange={(v) => patch({ frIsReducedCap: v })} />
            <PercentField label="IS français — taux normal" value={tax.frIsMainRate} onChange={(v) => patch({ frIsMainRate: v })} />
          </div>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={fieldStyle}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                Parts fiscales
                <InfoTip label="Parts fiscales" text={TAX_INFO.fiscalPartsSetting} />
              </span>
              <input
                style={inputStyle}
                defaultValue={String(tax.fiscalParts)}
                inputMode="decimal"
                onBlur={(e) => {
                  const parsed = parseAmount(e.target.value)
                  if (parsed !== null && parsed >= 0.5) patch({ fiscalParts: parsed })
                  else e.target.value = String(tax.fiscalParts)
                }}
              />
            </label>
            <PercentField label="Abattement frais pro" value={tax.salaryAllowanceRate} onChange={(v) => patch({ salaryAllowanceRate: v })} />
            <AmountField label="Plafond de l'abattement" value={tax.salaryAllowanceCap} onChange={(v) => patch({ salaryAllowanceCap: v })} />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--muted)', fontWeight: 600 }}>
              Barème IR (annuel, 1 part — la dernière tranche est « au-delà »)
            </div>
            {tax.irBrackets.map((bracket, i) => {
              const isLast = i === tax.irBrackets.length - 1
              return (
                <div key={`${i}-${bracket.upTo ?? 'last'}`} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.75rem', color: 'var(--muted)' }}>
                  {isLast ? (
                    <span style={{ width: '110px' }}>au-delà</span>
                  ) : (
                    <input
                      style={inputStyle}
                      defaultValue={String(bracket.upTo ?? '')}
                      inputMode="numeric"
                      aria-label={`Plafond de la tranche ${i + 1}`}
                      onBlur={(e) => {
                        const parsed = parseAmount(e.target.value)
                        if (parsed !== null && parsed > 0) {
                          const next = tax.irBrackets.map((b, j) => (j === i ? { ...b, upTo: parsed } : b))
                          patch({ irBrackets: next })
                        } else e.target.value = String(bracket.upTo ?? '')
                      }}
                    />
                  )}
                  <input
                    style={{ ...inputStyle, width: '70px' }}
                    defaultValue={String(Math.round(bracket.rate * 10000) / 100)}
                    inputMode="decimal"
                    aria-label={`Taux de la tranche ${i + 1}`}
                    onBlur={(e) => {
                      const parsed = parseAmount(e.target.value)
                      if (parsed !== null && parsed <= 100) {
                        const next = tax.irBrackets.map((b, j) => (j === i ? { ...b, rate: parsed / 100 } : b))
                        patch({ irBrackets: next })
                      } else e.target.value = String(Math.round(bracket.rate * 10000) / 100)
                    }}
                  />
                  <span>%</span>
                  {!isLast && (
                    <button
                      onClick={() => {
                        // Les clés de ligne sont `${i}-${upTo}` : avec deux plafonds identiques,
                        // ajouter ou supprimer une tranche peut faire collisionner deux clés d'un
                        // rendu à l'autre, et React réutilise alors un input monté avec le
                        // defaultValue d'une autre ligne. On remonte le corps du panneau pour que
                        // tous les defaultValue soient relus. Sans risque d'avaler le clic : il est
                        // déjà délivré (le blur du champ focalisé a commité avant ce onClick).
                        patch({ irBrackets: tax.irBrackets.filter((_, j) => j !== i) })
                        setResetEpoch((n) => n + 1)
                      }}
                      aria-label={`Supprimer la tranche ${i + 1}`}
                      style={{ background: 'transparent', border: 'none', color: 'var(--dim)', cursor: 'pointer', fontFamily: 'inherit' }}
                    >
                      ×
                    </button>
                  )}
                </div>
              )
            })}
            <button
              onClick={() => {
                const last = tax.irBrackets[tax.irBrackets.length - 1]
                const previous = tax.irBrackets[tax.irBrackets.length - 2]
                const newUpTo = (previous?.upTo ?? 10_000) * 2
                const inserted = { upTo: newUpTo, rate: last?.rate ?? 0.45 }
                patch({ irBrackets: [...tax.irBrackets.slice(0, -1), inserted, ...(last ? [last] : [])] })
                // Même raison qu'à la suppression : clés de ligne potentiellement en collision.
                setResetEpoch((n) => n + 1)
              }}
              style={{
                alignSelf: 'flex-start',
                background: 'var(--elevated)',
                border: '1px solid var(--line)',
                borderRadius: '6px',
                color: 'var(--muted)',
                fontSize: '0.75rem',
                fontFamily: 'inherit',
                padding: '0.25rem 0.625rem',
                cursor: 'pointer',
              }}
            >
              Ajouter une tranche
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', borderTop: '1px solid var(--line)', paddingTop: '0.75rem' }}>
            {/* La décote, le plafonnement du quotient familial et la CSG/CRDS non
                déductible sont implémentés depuis C1–C3 : les annoncer comme des
                limites était devenu faux. Ne restent que les limites réelles.
                « Avec le plafonnement de droit commun » en était une autre : dès
                2 parts la référence du moteur est le couple, donc aucune demi-part
                supplémentaire n'est plafonnée. */}
            <p data-testid="tax-limits" style={{ fontSize: '0.6875rem', color: 'var(--dim)', margin: 0, maxWidth: '32rem' }}>
              Limites : aucun autre revenu du foyer n’est pris en compte, et le foyer n’est décrit
              que par ses parts fiscales. À partir de 2 parts, le moteur bascule sur les règles du
              couple (décote « couple », référence 2 parts pour le plafonnement) — un parent isolé
              relèverait des règles de la personne seule et paierait davantage.
            </p>
            <button
              onClick={() => {
                onChange({ ...DEFAULT_FINANCE_SETTINGS.tax })
                setResetEpoch((n) => n + 1)
              }}
              style={{
                background: 'var(--elevated)',
                border: '1px solid var(--line)',
                borderRadius: '6px',
                color: 'var(--muted)',
                fontSize: '0.75rem',
                fontFamily: 'inherit',
                padding: '0.375rem 0.75rem',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              Réinitialiser les défauts
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
